import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { printOrder, qzErrorMessage } from '../lib/qzPrint';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { ShoppingCart, Printer, Eye, Check, Truck, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../App';
import { can } from '../lib/perms';
import { EditRecordDialog, EditedNote } from '../components/EditRecordDialog';
import { OrderShareActions } from '../components/OrderShareActions';
import { SortableHeader, applySorting } from '../components/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [printingId, setPrintingId] = useState(null);
  const { user } = useAuth();
  const canEdit = can(user, 'orders.edit');
  const canDelete = can(user, 'orders.delete');
  const canSend = can(user, 'orders.send');
  const [editingOrder, setEditingOrder] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const handleSort = (field, dir) => { setSortField(field); setSortDirection(dir); };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true })
      ]);
      setOrders(ordersRes.data);
      const openId = searchParams.get('order');
      if (openId) setSelectedOrder(ordersRes.data.find((o) => o.order_id === openId) || null);
      setSuppliers(suppliersRes.data);
      setBranches(branchesRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Order status updated');
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const handlePrint = async (order) => {
    setPrintingId(order.order_id);
    try {
      const printer = await printOrder(order.order_id);
      toast.success(`Sent to ${printer.name || printer.ip_address}`);
      if (order.status === 'pending') {
        await axios.put(`${API}/orders/${order.order_id}/status?status=printed`, {}, { withCredentials: true });
        fetchData();
      }
    } catch (e) { toast.error(qzErrorMessage(e)); }
    finally { setPrintingId(null); }
  };

  const closeOrder = () => {
    setSelectedOrder(null);
    if (searchParams.get('order')) setSearchParams({});
  };

  const handleSaveEdit = async (payload) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/orders/${editingOrder.order_id}`, payload, { withCredentials: true });
      toast.success('Order updated');
      setEditingOrder(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update order'); }
    finally { setSavingEdit(false); }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Delete order ${order.order_id.slice(-8)} for ${order.supplier_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/orders/${order.order_id}`, { withCredentials: true });
      toast.success('Order deleted');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete order'); }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSupplier = !filterSupplier || order.supplier_id === filterSupplier;
    const matchesBranch = !filterBranch || order.branch_id === filterBranch;
    const matchesStatus = !filterStatus || order.status === filterStatus;
    const matchesPo = !poSearch || (order.po_number || order.order_name || '').toLowerCase().includes(poSearch.toLowerCase());
    return matchesSupplier && matchesBranch && matchesStatus && matchesPo;
  });

  const sortedOrders = applySorting(filteredOrders, sortField, sortDirection, (item, field) => {
    if (field === 'items_count') return item.items?.length || 0;
    return item[field];
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'printed': return 'outline';
      case 'submitted': return 'default';
      case 'received': return 'success';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Purchase Orders
          </h1>
          <p className="text-muted-foreground mt-1">Manage and print supplier orders</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Input placeholder="Search PO number" value={poSearch} onChange={(e) => setPoSearch(e.target.value)}
              className="w-full md:w-48" data-testid="po-search-input" />
            <Select value={filterBranch || "all"} onValueChange={(val) => setFilterBranch(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full md:w-48" data-testid="order-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.branch_id} value={branch.branch_id}>{branch.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterSupplier || "all"} onValueChange={(val) => setFilterSupplier(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full md:w-48" data-testid="order-supplier-filter">
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>{supplier.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full md:w-48" data-testid="order-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="printed">Printed</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="received">Received</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No orders found</h3>
              <p className="text-muted-foreground">Purchase orders are created when you save a supplier stock take</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader label="PO Number" field="po_number" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Date" field="date" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Branch" field="branch_name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Supplier" field="supplier_name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Items" field="items_count" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Value" field="total_value" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Status" field="status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOrders.map((order) => (
                  <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                    <TableCell className="font-mono text-sm" data-testid={`order-name-${order.order_id}`}>{order.po_number || order.order_name || order.order_id?.slice(-8)}</TableCell>
                    <TableCell>
                      {new Date(order.date).toLocaleDateString()}
                      {order.edited_at && <p className="text-xs text-muted-foreground" data-testid={`order-edited-${order.order_id}`}>Edited by {order.edited_by}</p>}
                    </TableCell>
                    <TableCell>{order.branch_name}</TableCell>
                    <TableCell>{order.supplier_name}</TableCell>
                    <TableCell>{order.items?.length || 0}</TableCell>
                    <TableCell>R{order.total_value?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(order.status)}>{order.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedOrder(order)}
                          data-testid={`view-order-${order.order_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canSend && (<Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(order)}
                          disabled={printingId === order.order_id}
                          data-testid={`print-order-${order.order_id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>)}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => setEditingOrder(order)} data-testid={`edit-order-${order.order_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteOrder(order)} data-testid={`delete-order-${order.order_id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                        {order.status === 'printed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(order.order_id, 'submitted')}
                            data-testid={`submit-order-${order.order_id}`}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Submit
                          </Button>
                        )}
                        {order.status === 'submitted' && (
                          <Button
                            size="sm"
                            onClick={() => handleUpdateStatus(order.order_id, 'received')}
                            data-testid={`receive-order-${order.order_id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Received
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* View Order Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && closeOrder()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="order-modal-title">{selectedOrder ? `Purchase Order ${selectedOrder.po_number || selectedOrder.order_name || ''}` : 'Purchase Order'}</DialogTitle>
            <DialogDescription className="sr-only">Items, quantities and value for this order</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Supplier:</span>
                  <span className="ml-2 font-medium">{selectedOrder.supplier_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Branch:</span>
                  <span className="ml-2 font-medium">{selectedOrder.branch_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="ml-2 font-medium">{new Date(selectedOrder.date).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Confirmed by:</span>
                  <span className="ml-2 font-medium" data-testid="order-confirmed-by">{selectedOrder.created_by || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge className="ml-2" variant={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-center">SOH</TableHead>
                    <TableHead className="text-center">PAR</TableHead>
                    <TableHead className="text-center">Order</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedOrder.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">{item.unit}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{item.soh}</TableCell>
                      <TableCell className="text-center">{item.par}</TableCell>
                      <TableCell className="text-center font-medium">{item.order_qty}</TableCell>
                      <TableCell className="text-right">R{((item.order_qty || 0) * (item.cost_price || 0) / (item.order_unit_qty || 1)).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {selectedOrder.notes && (
                <p className="text-sm" data-testid="order-notes"><span className="text-muted-foreground">Notes:</span> {selectedOrder.notes}</p>
              )}
              <EditedNote record={selectedOrder} />
              {selectedOrder.emailed_at && (
                <p className="text-xs text-muted-foreground" data-testid="order-emailed-note">
                  Emailed to {selectedOrder.emailed_to?.join(', ')} by {selectedOrder.emailed_by} on {new Date(selectedOrder.emailed_at).toLocaleString()}
                </p>
              )}

              <div className="flex justify-between items-center pt-4 border-t">
                <span className="font-medium">Total Value:</span>
                <span className="text-xl font-bold">R{selectedOrder.total_value?.toFixed(2)}</span>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                {canSend && <OrderShareActions order={selectedOrder}
                  onSent={(u) => { setSelectedOrder((o) => ({ ...o, ...u })); fetchData(); }} />}
                {canSend && <Button
                  onClick={() => handlePrint(selectedOrder)}
                  disabled={printingId === selectedOrder.order_id}
                  data-testid="modal-print-direct-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  {printingId === selectedOrder.order_id ? 'Printing...' : 'Print Docket'}
                </Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditRecordDialog open={!!editingOrder} onOpenChange={(o) => !o && setEditingOrder(null)}
        record={editingOrder} mode="order" onSave={handleSaveEdit} saving={savingEdit} />
    </div>
  );
};

export default Orders;
